import React from 'react';
import { Form, Field } from 'react-final-form';
import map from 'lodash/map';
import styled from 'styled-components';
import date_helper from 'utils/date-helper';
import Button from '@joinforma/ui/components/Button';
import Caption from '@joinforma/ui/components/Caption';
import Checkbox from '@joinforma/ui/components/Checkbox';
import TextField from '@joinforma/ui/components/TextField';
import Select from '@joinforma/ui/components/Select';
import Option from '@joinforma/ui/components/Option';
import PhoneNumberField from '@joinforma/ui/components/PhoneNumber';
import DatePicker from '@joinforma/ui/components/DatePicker';
import { ScreenWidth } from '@joinforma/ui/constants/breakpoints';

import { FormaCardMember } from './types';

import { CardHolderType, CountryCode } from 'constants/unit';
import { Country } from 'constants/countries';
import { UsaState } from 'constants/locations';

import {
  ButtonSize,
  AutocompleteType,
  ButtonVariant,
} from '@joinforma/ui/constants/properties';

interface Props {
  mailing_address?: FormaCardMember['billing_address'];
  forma_card_member: FormaCardMember;
  employee_id: FormaCardMember['employee_id'];
  handleCancel: () => void;
  handleCreateFormaCard: ({
    card_details,
    card_type,
  }: {
    card_type: 'physical';
    card_details: {
      cardHolderType: CardHolderType;
      cardHolderReferenceId: string;
      cardHolderDetails: {
        fullName: {
          firstName: FormaCardMember['first_name'];
          lastName: FormaCardMember['last_name'];
        };
        dateOfBirth?: FormaCardMember['date_of_birth'];
        address: {
          street: string;
          city: string;
          state: string;
          postalCode: string;
          country: string;
        };
        shippingAddress: {
          street: string;
          city: string;
          state: string;
          postalCode: string;
          country: string;
        };
        phone: {
          countryCode: string;
          phoneNumber: string;
        };
        email?: string;
      };
    };
  }) => void;
}

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: 20px;
`;

const FormRow = styled.div`
  display: flex;
  gap: 15px;
  margin-bottom: 20px;
  width 100%;

  @media (max-width: ${ScreenWidth.TABLET_PORTRAIT}) {
    flex-direction: column;
  }
`;

const InputWrapper = styled.div`
  display: flex;
  width: 50%;

  @media (max-width: ${ScreenWidth.TABLET_PORTRAIT}) {
    width: 100%;
    margin: 5px 0;
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 22px;
  gap: 10px;
`;

const SubmitButton = styled(Button).attrs({
  variant: ButtonVariant.PRIMARY,
  size: ButtonSize.LARGE,
})``;

const CancelButton = styled(Button).attrs({
  variant: ButtonVariant.SECONDARY,
  size: ButtonSize.LARGE,
})``;

const StyledTextField = styled(TextField)`
  width: 100%;
`;

const StyledSelect = styled(Select)`
  width: 142px;
  margin: 0 15px 0 0;
`;

const DateOfBirthField = styled(DatePicker)`
  width: 60%;

  @media (max-width: ${ScreenWidth.TABLET_PORTRAIT}) {
    width: 100%;
  }
`;

const StyledPhoneNumberField = styled(PhoneNumberField)`
  @media (max-width: ${ScreenWidth.TABLET_PORTRAIT}) {
    width: 100%;
  }
`;

const DobConfirm = styled(Caption)`
  padding: 10px;
  color: ${({ theme }) => theme.colors.icon.warning};
`;

const IssueFormaCardForm = ({
  forma_card_member,
  mailing_address,
  employee_id,
  handleCancel,
  handleCreateFormaCard,
}: Props) => {
  const composeValidators =
    (...validators: Array<(v: string) => string | undefined>) =>
    (value: string) =>
      validators.reduce<string | undefined>(
        (error, validator) => error || validator(value),
        undefined
      );

  const required = (value: string | undefined) =>
    value ? undefined : 'This field cannot be blank';

  const isValidEmail = (value: string) =>
    value
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      )
      ? undefined
      : 'Please enter a valid email';

  const {
    id,
    first_name,
    last_name,
    date_of_birth: initial_date_of_birth,
    email,
  } = forma_card_member;

  const is_employee: boolean = employee_id === id;

  const formatedDateOfBirth = date_helper
    .utc(initial_date_of_birth)
    .format('MMM DD, YYYY');

  return (
    <Form
      onSubmit={({
        should_use_mailing_address,
        phone_number,
        mailing_address_locality,
        mailing_address_state,
        mailing_address_street,
        mailing_address_street_ext,
        mailing_address_zip,
        email,
        billing_address_street,
        billing_address_street_ext,
        billing_address_locality,
        billing_address_state,
        billing_address_zip,
        date_of_birth,
      }) => {
        // Handling logic for form submission
        const shipping_address = {
          street: mailing_address_street,
          street2: mailing_address_street_ext
            ? mailing_address_street_ext
            : undefined,
          city: mailing_address_locality,
          state: mailing_address_state,
          postalCode: mailing_address_zip,
          country: Country.US.toUpperCase(),
        };
        const billing_address = should_use_mailing_address
          ? shipping_address
          : {
              street: billing_address_street,
              street2: billing_address_street_ext
                ? billing_address_street_ext
                : undefined,
              city: billing_address_locality,
              state: billing_address_state,
              postalCode: billing_address_zip,
              country: Country.US.toUpperCase(),
            };

        const card_details = {
          cardHolderType: is_employee
            ? CardHolderType.MEMBER
            : CardHolderType.DEPENDENT,
          cardHolderReferenceId: id,
          cardHolderDetails: {
            fullName: {
              firstName: first_name,
              lastName: last_name,
            },
            dateOfBirth: initial_date_of_birth
              ? initial_date_of_birth
              : date_of_birth,
            address: billing_address,
            shippingAddress: shipping_address,
            phone: {
              countryCode: CountryCode.US,
              phoneNumber: phone_number.replace(/\D/g, ''),
            },
            email,
          },
        };
        handleCreateFormaCard({ card_details, card_type: 'physical' });
        handleCancel();
      }}
      render={({
        handleSubmit,
        values: {
          should_use_primary_residence_address,
          should_use_mailing_address,
        },
      }) => (
        <FormContainer onSubmit={handleSubmit}>
          {mailing_address?.street && (
            <FormRow>
              <Field
                name="should_use_primary_residence_address"
                initialValue
                type="checkbox"
                render={({ input }) => (
                  <Checkbox {...input}>
                    {is_employee
                      ? 'Use primary residence as a mailing address'
                      : 'Use primary residence as a mailing address for dependent'}
                  </Checkbox>
                )}
              />
            </FormRow>
          )}
          <FormRow>
            <Field
              name="mailing_address_street"
              initialValue={
                should_use_primary_residence_address
                  ? mailing_address?.street
                  : ''
              }
              validate={required}
              render={({ input, meta }) => (
                <InputWrapper>
                  <StyledTextField
                    {...input}
                    label="Mailing Address Line 1"
                    autocomplete={AutocompleteType.ADDRESS_LINE1}
                    placeholder="Enter your address"
                    error={meta.touched && meta.error}
                  />
                </InputWrapper>
              )}
            />
            <Field
              name="mailing_address_street_ext"
              initialValue={
                should_use_primary_residence_address
                  ? mailing_address?.street_ext
                  : ''
              }
              render={({ input, meta }) => (
                <InputWrapper>
                  <StyledTextField
                    {...input}
                    label="Mailing Address Line 2 (optional)"
                    autocomplete={AutocompleteType.ADDRESS_LINE2}
                    placeholder="Enter your unit"
                    error={meta.touched && meta.error}
                  />
                </InputWrapper>
              )}
            />
          </FormRow>
          <FormRow>
            <Field
              name="mailing_address_locality"
              validate={required}
              initialValue={
                should_use_primary_residence_address
                  ? mailing_address?.locality
                  : ''
              }
              render={({ input, meta }) => (
                <InputWrapper>
                  <StyledTextField
                    {...input}
                    label="City"
                    placeholder="Enter City"
                    error={meta.touched && meta.error}
                  />
                </InputWrapper>
              )}
            />
            <InputWrapper>
              <Field
                name="mailing_address_state"
                validate={required}
                initialValue={
                  should_use_primary_residence_address
                    ? mailing_address?.state
                    : ''
                }
                render={({ input, meta }) => (
                  <StyledSelect
                    {...input}
                    label="State"
                    error={meta.touched && meta.error}
                  >
                    {map(UsaState, (v, k) => (
                      <Option key={k} value={k}>
                        {k}
                      </Option>
                    ))}
                  </StyledSelect>
                )}
              />
              <Field
                name="mailing_address_zip"
                validate={required}
                initialValue={
                  should_use_primary_residence_address
                    ? mailing_address?.zip
                    : ''
                }
                render={({ input, meta }) => (
                  <StyledTextField
                    {...input}
                    label="Zip Code"
                    autocomplete={AutocompleteType.POSTAL_CODE}
                    placeholder="Enter Zip Code"
                    error={meta.touched && meta.error}
                  />
                )}
              />
            </InputWrapper>
          </FormRow>
          <FormRow>
            <Field
              name="email"
              initialValue={email}
              validate={composeValidators(required, isValidEmail)}
              render={({ input, meta }) => (
                <InputWrapper>
                  <StyledTextField
                    {...input}
                    label="Email"
                    disabled={!!email}
                    placeholder="Enter an email"
                    error={meta.touched && meta.error}
                  />
                </InputWrapper>
              )}
            />
          </FormRow>
          <FormRow>
            <Field
              name="should_use_mailing_address"
              initialValue={false}
              type="checkbox"
              render={({ input }) => (
                <Checkbox {...input}>
                  Billing address same as mailing address
                </Checkbox>
              )}
            />
          </FormRow>
          {!should_use_mailing_address && (
            <>
              <FormRow>
                <Field
                  name="billing_address_street"
                  validate={required}
                  render={({ input, meta }) => (
                    <InputWrapper>
                      <StyledTextField
                        {...input}
                        label="Billing Address Line 1"
                        autocomplete={AutocompleteType.ADDRESS_LINE1}
                        placeholder="Billing Address Line 1"
                        error={meta.touched && meta.error}
                      />
                    </InputWrapper>
                  )}
                />
                <Field
                  name="billing_address_street_ext"
                  render={({ input, meta }) => (
                    <InputWrapper>
                      <StyledTextField
                        {...input}
                        label="Billing Address Line 2"
                        autocomplete={AutocompleteType.ADDRESS_LINE2}
                        placeholder="Billing Address Line 2 (optional)"
                        error={meta.touched && meta.error}
                      />
                    </InputWrapper>
                  )}
                />
              </FormRow>
              <FormRow>
                <Field
                  name="billing_address_locality"
                  validate={required}
                  render={({ input, meta }) => (
                    <InputWrapper>
                      <StyledTextField
                        {...input}
                        label="City"
                        placeholder="Enter City"
                        error={meta.touched && meta.error}
                      />
                    </InputWrapper>
                  )}
                />
                <InputWrapper>
                  <Field
                    name="billing_address_state"
                    validate={required}
                    render={({ input, meta }) => (
                      <StyledSelect
                        {...input}
                        label="State"
                        error={meta.touched && meta.error}
                      >
                        {map(UsaState, (v, k) => (
                          <Option key={k} value={k}>
                            {k}
                          </Option>
                        ))}
                      </StyledSelect>
                    )}
                  />
                  <Field
                    name="billing_address_zip"
                    validate={required}
                    render={({ input, meta }) => (
                      <StyledTextField
                        {...input}
                        label="Zip Code"
                        autocomplete={AutocompleteType.POSTAL_CODE}
                        placeholder="Zip Code"
                        error={meta.touched && meta.error}
                      />
                    )}
                  />
                </InputWrapper>
              </FormRow>
            </>
          )}
          <FormRow>
            <Field
              name="date_of_birth"
              initialValue={formatedDateOfBirth}
              validate={required}
              render={({ input, meta }) => (
                <DateOfBirthField
                  {...input}
                  disabled={!!initial_date_of_birth}
                  label="Date of birth"
                  placeholder="Date of birth"
                  error={meta.touched && meta.error}
                />
              )}
            />
            {!initial_date_of_birth && (
              <DobConfirm>
                {`Please confirm ${
                  is_employee ? 'your' : 'the'
                } date of birth before submission`}
              </DobConfirm>
            )}
          </FormRow>
          <FormRow>
            <Field
              name="phone_number"
              validate={required}
              render={({ input, meta }) => (
                <StyledPhoneNumberField
                  {...input}
                  label="Phone Number"
                  placeholder="Phone number"
                  autocomplete={AutocompleteType.TEL_NATIONAL}
                  error={meta.touched && meta.error}
                />
              )}
            />
          </FormRow>
          <ButtonsContainer>
            <CancelButton onClick={handleCancel}>Cancel</CancelButton>
            <SubmitButton onClick={handleSubmit}>Confirm</SubmitButton>
          </ButtonsContainer>
        </FormContainer>
      )}
    />
  );
};

export default IssueFormaCardForm;
